import { z } from 'zod';
import { type AddressMeta, verifiedAddressMetaSchema } from './address';
import { dateOnlySchema, normalizePhone, uuidSchema } from './common';
import { TIME_PATTERN } from './time';

// ── Точка маршрута: где машина останавливается и что там делает ──
// План `docs/route-trips-plan.md`, этап 1. Модуль **аддитивный**: он стоит рядом с действующими
// контрактами маршрута (`vehicle-routes.ts`), ничего в них не переключая, — переключение это
// этап 2.
//
// Сегодня строка маршрута описывает одновременно две разные вещи: **потребность** заказчика
// («забрать с карьера, привезти на объект») и **движение** машины («заехал в карьер, потом на
// объект»). Пока у заявки одна поездка, они совпадают; как только поездок становится несколько,
// совпадение кончается: у заявки с ездками `A→B` и `A→C` «адрес разгрузки заявки» не существует, а
// две строки задания, начинающиеся в `A`, портал считает двумя заездами, хотя грузятся они за один.
//
// Поэтому вещи разводятся: потребность описывает **ездка** (строка заявки), движение — **точка**
// (строка маршрута), а связывает их **роль** на точке: погрузка, разгрузка, работа линейного дня
// (Р5, Р5а). Порядок объезда — это порядок точек, и он один на весь смешанный день: два рейса с
// песком и заезд экскаватором на площадку переставляются одними и теми же стрелками.
//
// Здесь — то, что обязано считаться одинаково на сервере и в браузере: тождество точки (по нему
// автосборка решает, заводить новый заезд или сесть на существующий), ответственные точки (от их
// порядка зависит, кого напечатает бланк, а кого свернёт в «+N») и инвариант «погрузка раньше
// разгрузки». Правило одно, а мест, где оно применяется, три: укладка заявки, карточка маршрута и
// выписка листа.

// ── Ссылка на строку задания ──

/**
 * Строка задания — ездка грузоперевозки. Ссылка составная, а не один `tripId`: точка живёт в
 * маршруте, а состав маршрута ведётся заявками, и заявку в ссылке приходится называть всюду, где
 * по ней берут блокировку или ищут строку состава.
 *
 * Разметка — `freight`, тем же словом, что у роли на точке и у строки бланка. Различают все три
 * объединения одно и то же — вид строки задания, — и разные слова на одно различие заставляли бы
 * каждое место сужения помнить, в каком из них оно сейчас: `action.kind === 'freight'`, а внутри
 * `action.ref.kind === 'trip'`. Одно слово на одно понятие.
 */
export type FreightTaskRef = { kind: 'freight'; requestId: string; tripId: string };

/**
 * Строка задания — день линейного заказа (Р5а, ADR 0100). Ездок у него нет вовсе: единица работы —
 * день, а не поездка, — поэтому день и лежит в самой ссылке. Второй копии дня рядом не держим: она
 * разошлась бы с составом при переносе рейса на другую дату (ADR 0082 тянет день каскадом).
 */
export type LinearTaskRef = { kind: 'linear'; requestId: string; workDate: string };

/**
 * Ссылка на строку задания. Не `tripId: string`: у линейного дня ездки нет, а адресовать его надо —
 * и предупреждениям, и блокерам, и порталу (Р5а, Р9б).
 */
export type TaskRef = FreightTaskRef | LinearTaskRef;

/**
 * Ключ ссылки для сравнения и дедупликации: `freight:<uuid>` либо `linear:<uuid>:<день>`.
 *
 * Заведён потому, что ссылку приходится складывать в `Set` и `Map` в каждом втором правиле —
 * инвариант порядка, неразложенные строки, предупреждения выписки, — а `JSON.stringify` объекта
 * зависит от порядка полей и молча дал бы два разных ключа для одной и той же строки. У ездки
 * заявка в ключ не идёт: `tripId` уникален сам по себе, а у линейного дня своего идентификатора
 * нет — его опознаёт пара «заявка + день».
 */
export function taskRefKey(ref: TaskRef): string {
  return ref.kind === 'freight'
    ? `freight:${ref.tripId}`
    : `linear:${ref.requestId}:${ref.workDate}`;
}

// ── Что делает машина на точке ──

interface PointActionBase {
  /** «ТС-40/2» либо «ТС-77 · 12.08» — только для показа. */
  displayNumber: string;
  /**
   * Порядок роли **внутри своей точки**, 1..N — «на карьере сначала грузим эту ездку, потом ту».
   *
   * Не украшение списка: две строки задания, стоящие на **одних и тех же** двух точках, порядком
   * объезда неразличимы (автосборка переиспользует точку по Р8, и первая с второй точки у них
   * общие). Прежде их различал номер заявки, то есть порядок заведения; а порядок строк задания —
   * это порядок талонов заказчиков (Р12, Р20), и решать его номером записи нельзя. Теперь решает
   * человек, и решает он ровно тем, что имеет физический смысл: чью ездку грузим на этой остановке
   * первой.
   *
   * Позиция уникальна внутри точки и связна (1..N): дыра означала бы снятую роль, которой никто не
   * заметил, — тем же правилом, каким уплотняются позиции точек (Р13).
   */
  position: number;
  /** Номера числами: ими задан порядок контактов и строк задания (Р11). */
  requestNum: number;
  /** Номер ездки внутри заявки; у линейного дня — 0. */
  tripNum: number;
  customerName: string;
  /** Ответственный этой роли: у ездки — контакт её конца, у дня — контакт заявки (Р9б). */
  contactName: string;
  contactPhone: string;
  /** Адрес строки разошёлся с адресом точки (Р10, Р9б). */
  addressMismatch: boolean;
}

/** Погрузка или разгрузка ездки: две точки маршрута на одну ездку, и обе обязательны (Р5). */
export interface FreightAction extends PointActionBase {
  kind: 'freight';
  /** Ссылка сужена веткой: `freight` с линейной ссылкой — состояние, которого не бывает. */
  ref: FreightTaskRef;
  role: 'load' | 'unload';
  cargoLabel: string;
  /** Позиция парной точки: куда поедет груз или откуда приехал. */
  pairPosition: number;
}

/** Работа линейного дня: одна точка, одна строка задания, пары у неё нет (Р5а). */
export interface LinearWorkAction extends PointActionBase {
  kind: 'linear';
  /** День лежит в самой ссылке — второй копии рядом не держим. */
  ref: LinearTaskRef;
  role: 'work';
}

/**
 * Что делает машина на точке. Дискриминатор — **вид строки задания**, а не роль: у ездки ролей две
 * (`load`/`unload`), у линейного дня одна (`work`), и различать надо именно строки — их по-разному
 * печатают, считают в ёмкость бланка и адресуют ссылкой.
 */
export type RoutePointAction = FreightAction | LinearWorkAction;

/**
 * Адрес так, как его несут и точка, и ездка: печатаемая строка плюс метаданные её происхождения
 * (ADR 0006, ADR 0069). Строка и метаданные ходят парой, потому что решают разное: строка идёт в
 * бумагу, метаданные опознают место (`addressKeyOf`).
 */
export interface AddressedLocation {
  location: string;
  address: AddressMeta | null;
}

export interface VehicleRoutePointDto extends AddressedLocation {
  id: string;
  /** Место в порядке объезда, 1..`MAX_ROUTE_POINTS`. */
  position: number;
  /** План прибытия «08:30»; пусто — не задан. */
  arrivalTime: string;
  /** Записка диспетчера водителю — про эту остановку, а не про заявку. */
  comment: string;
  /**
   * Что здесь происходит: погрузки, разгрузки и работа линейных дней — в порядке `position`, то
   * есть в том, в каком на этой остановке работают.
   */
  actions: RoutePointAction[];
  /**
   * Кто встречает машину на этой остановке (Р9): контакты ролей, дедуплицированные по
   * `contactKeyOf` — легаси-номер сравнивается своей строкой, пустой в сравнении не участвует.
   * Двое и больше — точку совместили вручную, и бланк печатает обоих (Р11а).
   */
  contacts: { name: string; phone: string }[];
}

// ── Тождество адреса ──

/**
 * Семантический ключ адреса. Три уровня, и порядок между ними — не вкусовщина:
 *
 * - `ref:` — запись справочника (объект, склад). Самое сильное опознание: две заявки, выбравшие
 *   один объект, приедут в одно место, даже если текст адреса у них набран по-разному;
 * - `fias:` — код ФИАС из подсказки DaData. Слабее справочника, но сильнее строки: один дом — один
 *   код, а написаний у него десяток;
 * - `text:` — нормализованная строка. Последний уровень для того, что заведено до жёсткой модели
 *   (ADR 0006) свободным вводом: сравнивать больше нечем.
 *
 * Ключ **семантический**, и расхождению адреса он не судья: у объекта справочника `ref:<uuid>` не
 * меняется, когда правят текст адреса, — а печатается именно текст. Поэтому `address_mismatch`
 * сравнивает печатаемые строки (Р9г), а этот ключ работает там, где вопрос стоит «то же ли это
 * место»: тождество точки (Р9) и подсказки совмещения (Р9а).
 */
export type AddressKey = `ref:${string}` | `fias:${string}` | `text:${string}`;

/**
 * Оформление строки адреса, а не её смысл: регистр, лишние пробелы и «ё». Последняя здесь не
 * прихоть — «Сычёво» и «Сычево» набирают оба, и без сведения одна и та же площадка развалилась бы
 * на два заезда.
 *
 * Знаки препинания **сохраняются**: «д. 5» и «д 5» сводятся пробелом, а вот выбрасывать точки и
 * запятые целиком значило бы приравнять «к.1» к «к1» — и заодно всё, что различается только
 * разделителем. Уровень `text:` и так последний, до него доходят только легаси-адреса, и лишняя
 * склейка здесь дороже лишнего заезда: заезд диспетчер совместит сам (Р9а), а склеенные точки он
 * сначала должен заметить.
 */
function normalizeAddressText(location: string): string {
  return location.trim().replace(/\s+/gu, ' ').toLowerCase().replace(/ё/gu, 'е');
}

/** Семантический ключ адреса (Р9): справочник, затем ФИАС, затем нормализованная строка. */
export function addressKeyOf(a: AddressedLocation): AddressKey {
  const refId = a.address?.refId;
  if (refId) return `ref:${refId}`;
  const fiasId = a.address?.fiasId;
  if (fiasId) return `fias:${fiasId}`;
  return `text:${normalizeAddressText(a.location)}`;
}

/**
 * Один и тот же адрес: справочник и ФИАС решают раньше строки.
 *
 * Это ровно равенство ключей, а не своя лесенка сравнений «оба из справочника? оба с ФИАС?».
 * Лесенка была бы **нетранзитивной**: адрес из справочника с ФИАС совпал бы и с чистым ФИАС-адресом
 * того же дома, и с другой записью справочника по тому же коду, а те двое между собой — нет. На
 * нетранзитивном отношении не собрать ни группу совмещения (`suggestPointMerges`), ни тождество
 * точки, у которого ключ обязан быть один на точку, — и подсказки зависели бы от того, с какой
 * точки начали сравнивать.
 */
export function sameRouteAddress(a: AddressedLocation, b: AddressedLocation): boolean {
  return addressKeyOf(a) === addressKeyOf(b);
}

// ── Тождество ответственного ──

/**
 * Ключ ответственного (Р9). Сравнивается телефон, а не имя: имя пишут свободно — «Иванов И.И.»,
 * «Иван Иванов», «прораб Иванов», — и одинаковых людей по нему не свести.
 *
 * Состояний три, и все три надо развести, иначе автосборка склеит разных людей:
 * `tel:` — номер сведён к десяти цифрам (ADR 0066); `raw:` — не сведён («доб. 12», два номера в
 * поле, короткий внутренний), и опознаётся собственной строкой; `null` — пусто.
 */
export type ContactKey = `tel:${string}` | `raw:${string}`;

/**
 * Ключ ответственного: нормализованный номер, иначе легаси-строка; пусто — ключа нет.
 *
 * Легаси-значение **нельзя** свести к «ключа нет»: `normalizePhone` отдаёт на нём `null`, но это не
 * «телефон не указан», а другой телефон — миграция `0095` такие записи не трогала (ADR 0066 п. 7).
 * Свести их к пустому значило бы объявить «Иванов, 8 (495) 123-45-67 доб. 12» и «Петров, 8 (495)
 * 123-45-67 доб. 14» одним человеком и молча собрать две разные площадки в один заезд.
 *
 * У `raw:` схлопываются регистр и пробелы — это оформление записи, а не её содержание. Дальше
 * строка идёт в ключ как есть, поэтому ключ и упаковывается JSON-ом (`pointIdentityKey`).
 */
export function contactKeyOf(phone: string): ContactKey | null {
  const trimmed = phone.trim();
  if (trimmed === '') return null;
  const local = normalizePhone(trimmed);
  if (local !== null) return `tel:${local}`;
  return `raw:${trimmed.replace(/\s+/gu, ' ').toLowerCase()}`;
}

// ── Тождество точки ──

/**
 * Точка глазами тождества: адрес и телефоны тех, кто на ней встречает. Имя не участвует (см.
 * `contactKeyOf`), позиция и время — тем более: два заезда в один карьер к одному весовщику это
 * одно и то же место, и различает их только решение человека ехать дважды (Р4).
 */
export interface PointIdentitySource extends AddressedLocation {
  contacts: readonly { phone: string }[];
}

/**
 * Тождество точки: адрес **и** множество ключей ответственных (Р9). Один адрес — не одно и то же
 * место работы: на площадке два прораба на разных корпусах, на карьере весовщик и кладовщик, и
 * приехать «к Иванову» и «к Петрову» по одному адресу значит приехать в разные места.
 *
 * Множество канонизируется — дедупликация и сортировка, — иначе тождество зависело бы от порядка
 * строк, в котором их вернул SQL, и одни и те же две точки то склеивались бы, то нет. Сортировка
 * обычная, по кодовым единицам: `localeCompare` зависит от локали среды, а ключ обязан совпасть
 * посчитанный на сервере и в браузере.
 *
 * Упаковка — именно JSON, а не склейка через разделитель: `raw:`-ключ несёт произвольный текст, и
 * два ключа «12345» и «678901» дали бы ту же строку, что один легаси-номер «12345,raw:678901».
 */
export function pointIdentityKey(p: PointIdentitySource): string {
  const keys = p.contacts
    .map((c) => contactKeyOf(c.phone))
    .filter((key): key is ContactKey => key !== null);
  return JSON.stringify([addressKeyOf(p), [...new Set(keys)].sort()]);
}

/** Та же точка по адресу и ответственным (Р9): на таких садится автосборка (Р8). */
export function samePointIdentity(a: PointIdentitySource, b: PointIdentitySource): boolean {
  return pointIdentityKey(a) === pointIdentityKey(b);
}

// ── Ответственные точки ──

/**
 * Порядок ролей в графе «заказчик, телефон» (Р9б): сначала те, кто отдаёт груз, потом работа
 * линейных дней, потом те, кто принимает. Он повторяет порядок граф «откуда» и «куда» той же
 * строки задания — водитель читает бумагу сверху вниз в том порядке, в котором поедет.
 */
const ROLE_ORDER: Record<RoutePointAction['role'], number> = { load: 0, work: 1, unload: 2 };

/** Ключ имени для тех контактов, у кого номера нет: то же оформление, что и у `raw:`-ключа. */
function nameOnlyKey(name: string): string {
  return `name:${name.replace(/\s+/gu, ' ').toLowerCase()}`;
}

/**
 * Ответственные точки: контакты её ролей без повторов по `contactKeyOf`.
 *
 * Порядок задан и **не зависит** от того, как строки вернул SQL: погрузки, затем работа линейных
 * дней, затем разгрузки (`load → work → unload`, Р9б); внутри роли — по её позиции на точке. От
 * порядка зависит, кто напечатан в бланке, а кто свернулся в «+N» (Р11а), поэтому «как пришло»
 * здесь не годится.
 *
 * **Почему внутри роли решает позиция, а не номер заявки, как было прежде.** Роли одного вида на
 * одной остановке — это две погрузки, и вопрос «чей ответственный встретит машину первым» есть тот
 * же самый вопрос «какую ездку грузим первой», на который отвечает `position`. Номер заявки на него
 * не отвечал вовсе — он говорил лишь, какую заявку завели раньше, и держался в ключе только ради
 * детерминизма. Заменяя бессмысленный хвост осмысленным, порядок графы «заказчик, телефон»
 * перестаёт расходиться с порядком работы на точке.
 *
 * **А вот `load → work → unload` позиции не подчиняется**, и это не непоследовательность.
 * Ответственные печатаются в строке задания сначала от точки «откуда», потом от «куда» (Р11а), и
 * первым в графе обязан стоять тот, кто относится к концу **этой** строки: приехав грузиться,
 * водитель звонит грузящему. Подчини мы старший ключ позиции — на остановке, где чужую ездку
 * разгружают раньше, чем грузят нашу, в графу «откуда» первым попал бы ответственный чужой
 * разгрузки, а наш свернулся бы в «+N». Позиция отвечает на «в каком порядке здесь работают»,
 * ролевой порядок — на «кто из них относится к печатаемому концу»; это разные вопросы.
 *
 * Один ключ, два разных имени («Иванов И.И.» и «прораб Иванов») — берётся имя первого вхождения в
 * этом же порядке: выдумывать, какое из написаний правильнее, портал не берётся.
 *
 * Контакт без номера в дедупликации по телефону не участвует — сравнивать нечем, — но и не
 * выбрасывается, пока у него есть имя: у заявок старше миграции `0062` номера нет вовсе, а имя
 * ответственного — единственное, что водитель о встречающем знает, и терять его молча нельзя.
 * Повторить одну и ту же безномерную запись дважды в одной графе, впрочем, тоже незачем, поэтому
 * между собой такие контакты сводятся по имени. Пустой целиком (ни имени, ни номера) не идёт в
 * список вовсе: печатать нечего и звонить некому.
 */
export function pointContacts(
  actions: readonly RoutePointAction[],
): { name: string; phone: string }[] {
  const ordered = [...actions].sort(
    (a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role] || a.position - b.position,
  );
  const seen = new Set<string>();
  const contacts: { name: string; phone: string }[] = [];
  for (const action of ordered) {
    const name = action.contactName.trim();
    const phone = action.contactPhone.trim();
    const key = contactKeyOf(phone) ?? (name === '' ? null : nameOnlyKey(name));
    if (key === null || seen.has(key)) continue;
    seen.add(key);
    contacts.push({ name, phone });
  }
  return contacts;
}

// ── Подсказки совмещения ──

/**
 * Что предложить совместить: точки одного адреса (Р9а). Полное тождество (адрес и ответственные) —
 * это то, что склеивает автосборка сама; совпал только адрес — предложение с оговоркой «у точек
 * разные ответственные, в лист пойдут оба».
 *
 * `pointIds` идут по возрастанию позиции, и первый из них — та точка, в которую переедут роли
 * (§7 плана): «первая по позиции» это свойство маршрута, а не выбор человека.
 *
 * Группа, а не пара: три заезда в один карьер это одно предложение «совместить», а не три попарных,
 * из которых человек собирал бы результат сам. Полностью тождественные точки из подсказок не
 * выбрасываются: автосборка склеивает только при укладке заявки, а после ручного разнесения (Р9а)
 * две одинаковые точки в маршруте стоять могут — и предложить свести их обратно правильно.
 */
export function suggestPointMerges(
  points: readonly VehicleRoutePointDto[],
): { pointIds: string[]; sameContacts: boolean }[] {
  const groups = new Map<AddressKey, VehicleRoutePointDto[]>();
  for (const point of [...points].sort((a, b) => a.position - b.position)) {
    const key = addressKeyOf(point);
    const group = groups.get(key);
    if (group) group.push(point);
    else groups.set(key, [point]);
  }
  const suggestions: { pointIds: string[]; sameContacts: boolean }[] = [];
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    const identities = new Set(group.map((point) => pointIdentityKey(point)));
    suggestions.push({
      pointIds: group.map((point) => point.id),
      sameContacts: identities.size === 1,
    });
  }
  return suggestions;
}

// ── Инвариант порядка ──

/** Где ездка стоит в маршруте: позиции обоих её концов, пока они найдены. */
interface TripPlacement {
  ref: FreightTaskRef;
  requestNum: number;
  tripNum: number;
  load: number | null;
  unload: number | null;
}

/**
 * Ездки, у которых разгрузка стоит раньше погрузки (Р6). Пусто — порядок цел.
 *
 * Между погрузкой и разгрузкой груз в кузове, поэтому позиция погрузки обязана быть **строго**
 * меньше: совпадение позиций — это «выгрузили там же, где грузили», то есть ездка, которой не было.
 * Линейного дня инвариант не касается вовсе: пары у него нет (Р5а).
 *
 * Считается по позициям точек, а не по `pairPosition` роли: позиция — то, что человек только что
 * переставил, а `pairPosition` уже посчитанное значение, и проверять инвариант по нему значило бы
 * проверять собственный вывод. Ездка, у которой в маршруте нашёлся только один конец, нарушением
 * порядка **не** считается — она не разложена, и это другой отказ (`rows_unplaced`).
 *
 * Порядок ответа — по номерам заявки и ездки: тем же ключом упорядочены строки задания (Р11), и
 * сообщение «ТС-40/2 разгружается раньше, чем грузится» человек ищет глазами в этом же порядке.
 */
export function brokenTripOrder(points: readonly VehicleRoutePointDto[]): TaskRef[] {
  const placements = new Map<string, TripPlacement>();
  for (const point of [...points].sort((a, b) => a.position - b.position)) {
    for (const action of point.actions) {
      if (action.kind !== 'freight') continue;
      const key = taskRefKey(action.ref);
      const placement = placements.get(key) ?? {
        ref: action.ref,
        requestNum: action.requestNum,
        tripNum: action.tripNum,
        load: null,
        unload: null,
      };
      // Роль у ездки одна на конец (`UNIQUE (trip_id, role)`), но если данные пришли битыми,
      // значима самая ранняя точка: именно она отвечает на «а когда груз оказался в кузове».
      if (action.role === 'load') placement.load ??= point.position;
      else placement.unload ??= point.position;
      placements.set(key, placement);
    }
  }
  return [...placements.values()]
    .filter((p) => p.load !== null && p.unload !== null && p.unload <= p.load)
    .sort((a, b) => a.requestNum - b.requestNum || a.tripNum - b.tripNum)
    .map((p) => p.ref);
}

// ── Схемы ──

/**
 * Версия маршрута: оптимистическая блокировка, одна на весь агрегат (Р16). Второй блокировки
 * внутри точек не заводим — она рассинхронизировалась бы при первом же совмещении, которое трогает
 * сразу несколько строк.
 *
 * Копия непубличной `versionSchema` из `vehicle-routes.ts`: этап 1 чужих файлов не трогает. На
 * этапе 2 у неё одно место на оба модуля.
 */
const versionSchema = z.coerce.number().int().min(0);

/**
 * Мест под точки в маршруте — CHECK позиции 1..20 (§5.2 плана): у каждой из десяти строк состава
 * (`MAX_ROUTE_REQUESTS`) не больше двух точек, погрузки и разгрузки. Числом, а не выводом из
 * соседнего модуля: на этапе 2 `VehicleRouteDto` получит точки, и импорт значения оттуда сюда
 * замкнул бы модули в цикл ради одной константы.
 */
export const MAX_ROUTE_POINTS = 20;

/**
 * Ролей на одной точке — столько же: предельный случай это день, который весь грузится и
 * разгружается в одном месте. Больше ролей, чем строк задания на два конца, в маршруте не бывает.
 */
export const MAX_POINT_ROLES = MAX_ROUTE_POINTS;

/**
 * Адрес остановки — снимок (Р10), а не ссылка на ездку: на точке сходятся ездки разных заявок, и
 * «адрес ездки» у неё неоднозначен. Предел тот же, что у адреса заявки (`locationSchema`,
 * 1000 знаков): это одна и та же строка, только слоем позже.
 */
const pointLocationSchema = z.string().trim().min(1, 'Укажите адрес точки').max(1000);

/** Что показать человеку, когда время прибытия набрано не так: не «неверно», а какого вида ждут. */
export const ARRIVAL_TIME_MESSAGE = 'Время прибытия в формате ЧЧ:ММ';

/**
 * План прибытия. Пустая строка законна и означает «не задан»: время остановки диспетчер знает не
 * всегда, а требовать его значило бы заставлять выдумывать час, который потом напечатают.
 */
const arrivalTimeSchema = z
  .string()
  .trim()
  .refine((v) => v === '' || TIME_PATTERN.test(v), ARRIVAL_TIME_MESSAGE)
  .optional()
  .default('');

/**
 * Роль на точке: какая строка задания здесь обслуживается и что с ней делают.
 *
 * Разметка по виду строки, а не по роли, — та же, что у `RoutePointAction`. У линейного дня роль
 * не спрашивается вовсе: она всегда `work` (Р5а), а поле с единственным допустимым значением
 * только даёт способ прислать невозможное сочетание — ровно то, что CHECK
 * `route_point_trips_role_trip_check` отвергает на другом конце.
 */
export const pointRoleSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('freight'),
      /** Заявка называется рядом с ездкой: по ней сервер берёт строку состава и блокировку (Р17). */
      requestId: uuidSchema,
      tripId: uuidSchema,
      role: z.enum(['load', 'unload']),
    })
    .strict(),
  z
    .object({
      kind: z.literal('linear'),
      requestId: uuidSchema,
      /** День линейного заказа: он же ключ строки состава (`work_date`, ADR 0100). */
      workDate: dateOnlySchema,
    })
    .strict(),
]);
export type PointRoleInput = z.infer<typeof pointRoleSchema>;

/** Ключ роли внутри запроса: им ловится «одна и та же роль названа дважды». */
function pointRoleKey(role: PointRoleInput): string {
  return role.kind === 'freight'
    ? `freight:${role.tripId}:${role.role}`
    : `linear:${role.requestId}:${role.workDate}`;
}

/** Одна и та же роль дважды в списке — не «уточнение», а расхождение с тем, что примет база. */
function rolesAreUnique(roles: readonly PointRoleInput[]): boolean {
  return new Set(roles.map(pointRoleKey)).size === roles.length;
}

const ROLES_NOT_UNIQUE_MESSAGE = 'Роль не может стоять на точке дважды';

/**
 * Завести точку и править её: адрес, время, комментарий и состав ролей **целиком**.
 *
 * Роли передаются полным списком, а не добавлением по одной: состав точки — это то, что человек
 * видит в карточке одним блоком, и частичная правка потребовала бы отдельного языка команд
 * («убрать погрузку ТС-40/1») там, где хватает списка.
 *
 * Хотя бы одна роль обязательна (Р13): точка без задания не заводится и не остаётся — «просто
 * заехать» бланк не печатает, а пустая остановка в порядке объезда сбивает счёт позиций.
 *
 * Адрес требуется верифицированным, как и у заявки (ADR 0006): именно он печатается в бланк
 * (Р11б) и именно по нему поедет машина. Снимок легаси-адреса в базе при этом остаётся — жёсткая
 * модель действует на **запись**, а не на чтение.
 */
export const routePointSchema = z
  .object({
    location: pointLocationSchema,
    address: verifiedAddressMetaSchema,
    arrivalTime: arrivalTimeSchema,
    /** Записка водителю про эту остановку: «звонить с ворот», «пропуск у весовщика». */
    comment: z.string().trim().max(2000).optional().default(''),
    roles: z.array(pointRoleSchema).min(1, 'Точка без задания не заводится').max(MAX_POINT_ROLES),
    version: versionSchema,
  })
  .strict()
  .refine((v) => rolesAreUnique(v.roles), {
    message: ROLES_NOT_UNIQUE_MESSAGE,
    path: ['roles'],
  });
export type RoutePointInput = z.infer<typeof routePointSchema>;
export type RoutePointBody = z.input<typeof routePointSchema>;

/**
 * Новый порядок объезда — полным списком, а не парой «подвинуть вверх» (Р14): сервер переписывает
 * позиции целиком, и две одновременные перестановки не соберут дыр в нумерации. Тем же порядком
 * устроена перестановка заявок в составе (`routeOrderSchema`).
 *
 * Что список — это ровно все точки маршрута, проверяет сервер: удалять и заводить точки
 * перестановкой нельзя, у того и другого своя ручка со своими правилами (Р13).
 */
export const pointOrderSchema = z
  .object({
    pointIds: z.array(uuidSchema).min(1).max(MAX_ROUTE_POINTS),
    version: versionSchema,
  })
  .strict()
  .refine((v) => new Set(v.pointIds).size === v.pointIds.length, {
    message: 'Точка не может стоять в маршруте дважды',
    path: ['pointIds'],
  });
export type PointOrderInput = z.infer<typeof pointOrderSchema>;
export type PointOrderBody = z.input<typeof pointOrderSchema>;

/**
 * Новый порядок ролей **внутри одной точки** — полным списком, тем же приёмом, что и порядок
 * объезда (Р14): сервер переписывает позиции целиком, и две одновременные перестановки не соберут
 * дыр в нумерации.
 *
 * Зачем эта дверь вообще есть. Порядок строк задания — это порядок талонов заказчиков (Р12, Р20), и
 * задаёт его порядок объезда. Но две строки, стоящие на **одних и тех же** точках, объезд не
 * различает: у обеих одна первая точка и одна вторая (Р8 переиспользует точку). Прежде их
 * разводил номер заявки — то есть никто; теперь их разводит человек, отвечая на осмысленный вопрос
 * «какую из ездок грузим на этой остановке первой».
 *
 * Что список — это ровно все роли точки, проверяет сервер: заводить и снимать роли перестановкой
 * нельзя, у того и другого своя дверь (правка состава точки и разнесение).
 */
export const pointRoleOrderSchema = z
  .object({
    roles: z
      .array(pointRoleSchema)
      .min(1, 'Порядок присылается полным списком ролей точки')
      .max(MAX_POINT_ROLES),
    version: versionSchema,
  })
  .strict()
  .refine((v) => rolesAreUnique(v.roles), {
    message: ROLES_NOT_UNIQUE_MESSAGE,
    path: ['roles'],
  });
export type PointRoleOrderInput = z.infer<typeof pointRoleOrderSchema>;
export type PointRoleOrderBody = z.input<typeof pointRoleOrderSchema>;

/**
 * Совместить точки в одну остановку (Р9а): роли переезжают в первую по позиции.
 *
 * Цель отдельным полем не спрашивается: «первая по позиции» — свойство маршрута, а не выбор
 * клиента, и разойтись эти два ответа не должны. Совпадение адресов проверяет сервер тем же
 * `sameRouteAddress`, которым портал строит подсказки: разные ответственные совмещению не помеха
 * (точка выйдет с обоими, и бланк напечатает обоих, Р11а), а разный адрес — помеха, потому что
 * машина не может приехать в два места сразу.
 */
export const mergePointsSchema = z
  .object({
    pointIds: z
      .array(uuidSchema)
      .min(2, 'Совмещать нечего: назовите хотя бы две точки')
      .max(MAX_ROUTE_POINTS),
    version: versionSchema,
  })
  .strict()
  .refine((v) => new Set(v.pointIds).size === v.pointIds.length, {
    message: 'Точка не может стоять в маршруте дважды',
    path: ['pointIds'],
  });
export type MergePointsInput = z.infer<typeof mergePointsSchema>;
export type MergePointsBody = z.input<typeof mergePointsSchema>;

/**
 * Разнести точку надвое (Р9а): названные роли уходят в новую точку сразу за исходной.
 *
 * Адрес и метаданные новая точка берёт у исходной — разносят не место, а работу: «этих грузим на
 * первом корпусе, тех на третьем» остаётся одним адресом ровно до того, как человек его уточнит.
 * Время прибытия и комментарий у новой точки свои и пустые: они описывают заезд, а заездов теперь
 * два.
 *
 * Что уходящих ролей не все, проверяет сервер: исходная точка не должна опустеть (Р13), а её
 * полный состав под блокировкой знает он один.
 */
export const splitPointSchema = z
  .object({
    roles: z
      .array(pointRoleSchema)
      .min(1, 'Укажите, что уходит в новую точку')
      .max(MAX_POINT_ROLES),
    version: versionSchema,
  })
  .strict()
  .refine((v) => rolesAreUnique(v.roles), {
    message: ROLES_NOT_UNIQUE_MESSAGE,
    path: ['roles'],
  });
export type SplitPointInput = z.infer<typeof splitPointSchema>;
export type SplitPointBody = z.input<typeof splitPointSchema>;
